import { Fragment, Suspense, useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/i18n/LanguageContext";
import AutoLocaleRedirect from "@/i18n/AutoLocaleRedirect";
import { LOCALE_CODES, DEFAULT_LOCALE } from "@/i18n/config";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import {
  Platform, Integrations, Contact as ContactPage,
  GlossaryIndex, GlossaryTerm,
} from "./pages/stubs";

const ModulesOverview = lazy(() => import("./pages/ModulesOverview"));
const ModuleDetail = lazy(() => import("./pages/ModuleDetail"));
const ToolsManagement = lazy(() => import("./pages/ToolsManagement"));
const CopperCaseStudy = lazy(() => import("./pages/CopperCaseStudy"));
const IndustryTemplate = lazy(() => import("./pages/IndustryTemplate"));
const LeanHub = lazy(() => import("./pages/LeanHub"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const Resources = lazy(() => import("./pages/Resources"));
const MesVsErp = lazy(() => import("./pages/resources/MesVsErp"));
const Hardware = lazy(() => import("./pages/Hardware"));
const HardwareDetail = lazy(() => import("./pages/HardwareDetail"));
const PrivacyNotice = lazy(() => import("./pages/legal/PrivacyNotice"));
const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"));


// Lazy load non-critical pages
const Login = lazy(() => import("./pages/Login"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Industries = lazy(() => import("./pages/Industries"));
const IndustryDetail = lazy(() => import("./pages/IndustryDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const News = lazy(() => import("./pages/News"));
const NewsDetail = lazy(() => import("./pages/NewsDetail"));
const Partners = lazy(() => import("./pages/Partners"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SIRI = lazy(() => import("./pages/SIRI"));
const SurveyPublic = lazy(() => import("./pages/SurveyPublic"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));

// Lazy load admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const IndustriesAdmin = lazy(() => import("./pages/admin/IndustriesAdmin"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const NewsAdmin = lazy(() => import("./pages/admin/NewsAdmin"));
const SurveysAdmin = lazy(() => import("./pages/admin/SurveysAdmin"));
const SurveyEditor = lazy(() => import("./pages/admin/SurveyEditor"));
const SurveyResponses = lazy(() => import("./pages/admin/SurveyResponses"));
const PartnersAdmin = lazy(() => import("./pages/admin/PartnersAdmin"));
const UsersAdmin = lazy(() => import("./pages/admin/UsersAdmin"));
const RequestsAdmin = lazy(() => import("./pages/admin/RequestsAdmin"));
const MediaAdmin = lazy(() => import("./pages/admin/MediaAdmin"));
const CaseStudiesAdmin = lazy(() => import("./pages/admin/CaseStudiesAdmin"));
const ProfileAdmin = lazy(() => import("./pages/admin/ProfileAdmin"));
const LegalReview = lazy(() => import("./pages/admin/LegalReview"));
const AccessLog = lazy(() => import("./pages/admin/AccessLog"));
const PricingAdmin = lazy(() => import("./pages/admin/PricingAdmin"));
const TranslationsAdmin = lazy(() => import("./pages/admin/TranslationsAdmin"));
const CategoriesAdmin = lazy(() => import("./pages/admin/CategoriesAdmin"));
const IntegrationPagesAdmin = lazy(() => import("./pages/admin/IntegrationPagesAdmin"));
const CollectionsAdmin = lazy(() => import("./pages/admin/CollectionsAdmin"));
const SystemIntegrationsAdmin = lazy(() => import("./pages/admin/SystemIntegrationsAdmin"));
const IntegrationDetail = lazy(() => import("./pages/IntegrationDetail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));



/** All public routes, rendered once per locale (English has no prefix). */
const publicRoutes = (p: string) => (
  <Fragment key={p || "en"}>
        <Route path={p || "/"} element={<Index />} />
        <Route path={`${p}/industries`} element={<Industries />} />
        {/* Preconfigured industry templates (static) — must come before dynamic :slug */}
        <Route path={`${p}/industries/cable-wire-manufacturing`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/fiber-optic-cable-manufacturing`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/plastics-extrusion`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/discrete-manufacturing`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/switchgear-electrical-panels`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/metal-processing-fabrication`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/food-beverage-packaging`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/packaging-printing-converting`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/automotive-components-assembly`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/wood-furniture-fitout`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/energy-intensive-manufacturing`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/manufacturing-holdings`} element={<IndustryDetail />} />
        <Route path={`${p}/industries/small-mid-size-manufacturers`} element={<IndustryDetail />} />
        {/* Back-compat redirects for previous slugs */}
        <Route path={`${p}/industries/cable-wire`} element={<Navigate to={`${p}/industries/cable-wire-manufacturing`} replace />} />
        <Route path={`${p}/industries/metal-processing`} element={<Navigate to={`${p}/industries/metal-processing-fabrication`} replace />} />
        <Route path={`${p}/industries/food-packaging`} element={<Navigate to={`${p}/industries/food-beverage-packaging`} replace />} />
        <Route path={`${p}/industries/custom-manufacturing`} element={<Navigate to={`${p}/industries/wood-furniture-fitout`} replace />} />
        <Route path={`${p}/industries/:slug`} element={<IndustryDetail />} />
        <Route path={`${p}/blog`} element={<Blog />} />
        <Route path={`${p}/blog/:slug`} element={<BlogPost />} />
        <Route path={`${p}/case-studies`} element={<CaseStudies />} />
        <Route path={`${p}/case-studies/copper-savings-die-management`} element={<CopperCaseStudy />} />
        <Route path={`${p}/case-studies/:slug`} element={<CaseStudyDetail />} />
        <Route path={`${p}/news`} element={<News />} />
        <Route path={`${p}/news/:slug`} element={<NewsDetail />} />
        <Route path={`${p}/partners`} element={<Partners />} />
        <Route path={`${p}/about`} element={<AboutUs />} />
        <Route path={`${p}/siri`} element={<SIRI />} />
        <Route path={`${p}/survey/:slug`} element={<SurveyPublic />} />
        <Route path={`${p}/contact`} element={<ContactPage />} />
        <Route path={`${p}/get-started`} element={<ContactPage />} />

        {/* BEEDIGIT platform & products */}
        <Route path={`${p}/platform`} element={<Platform />} />
        <Route path={`${p}/hardware`} element={<Hardware />} />
        <Route path={`${p}/hardware/:slug`} element={<HardwareDetail />} />
        <Route path={`${p}/integrations`} element={<Integrations />} />
        <Route path={`${p}/integrations/:slug`} element={<IntegrationDetail />} />
        <Route path={`${p}/modules`} element={<ModulesOverview />} />
        <Route path={`${p}/modules/tools-management`} element={<ToolsManagement />} />
        <Route path={`${p}/modules/:slug`} element={<ModuleDetail />} />
        <Route path={`${p}/lean`} element={<LeanHub />} />
        <Route path={`${p}/pricing`} element={<Pricing />} />
        <Route path={`${p}/roi-calculator`} element={<ROICalculator />} />
        <Route path={`${p}/resources`} element={<Resources />} />
        <Route path={`${p}/resources/mes-vs-erp-comparison`} element={<MesVsErp />} />

        {/* Legacy /products/* → /modules/* */}
        <Route path={`${p}/products/monitoring-oee`} element={<Navigate to={`${p}/modules/oee-downtime`} replace />} />
        <Route path={`${p}/products/manufacturing-execution`} element={<Navigate to={`${p}/modules/mes`} replace />} />
        <Route path={`${p}/products/energy-management`} element={<Navigate to={`${p}/modules/energy-management`} replace />} />
        <Route path={`${p}/products/quality-management`} element={<Navigate to={`${p}/modules/quality-management`} replace />} />
        <Route path={`${p}/products/cmms`} element={<Navigate to={`${p}/modules/cmms-maintenance`} replace />} />
        <Route path={`${p}/products/workspace`} element={<Navigate to={`${p}/modules/dashboards-ai`} replace />} />
        <Route path={`${p}/glossary`} element={<GlossaryIndex />} />
        <Route path={`${p}/glossary/:slug`} element={<GlossaryTerm />} />

        {/* Legal */}
        <Route path={`${p}/privacy`} element={<PrivacyNotice />} />
        <Route path={`${p}/terms`} element={<TermsOfUse />} />
        {/* Legacy Webflow path */}
        <Route path={`${p}/beedigit-privacy-notice`} element={<Navigate to={`${p}/privacy`} replace />} />

  </Fragment>
);

/** Loads analytics only if a stored acceptance exists, and reports SPA page views. */
const AnalyticsBridge = () => {
  const location = useLocation();
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

const AppRoutes = () => (
  <LanguageProvider>
    <AnalyticsBridge />
    <ScrollToTop />
    <AutoLocaleRedirect />
    <CookieConsent />
    <ErrorBoundary>
    <Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        <Route path="/sitemap.xml" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

        {/* English (default, no prefix) */}
        {publicRoutes("")}

        {/* Localized routes: /ar, /pl, /uk, /de, /uz, /th, /hi, /es, /fr */}
        {LOCALE_CODES.filter((c) => c !== DEFAULT_LOCALE).map((code) => publicRoutes(`/${code}`))}

        {/* Admin routes (English only) */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="requests" element={<RequestsAdmin />} />
          <Route path="industries" element={<IndustriesAdmin />} />
          <Route path="blog" element={<BlogAdmin />} />
          <Route path="case-studies" element={<CaseStudiesAdmin />} />
          <Route path="news" element={<NewsAdmin />} />
          <Route path="surveys" element={<SurveysAdmin />} />
          <Route path="surveys/:id" element={<SurveyEditor />} />
          <Route path="surveys/:id/responses" element={<SurveyResponses />} />
          <Route path="partners" element={<PartnersAdmin />} />
          <Route path="media" element={<MediaAdmin />} />
          <Route path="users" element={<UsersAdmin />} />
          <Route path="profile" element={<ProfileAdmin />} />
          <Route path="legal-review" element={<LegalReview />} />
          <Route path="access-log" element={<AccessLog />} />
          <Route path="pricing" element={<PricingAdmin />} />
          <Route path="translations" element={<TranslationsAdmin />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="integrations" element={<IntegrationPagesAdmin />} />
          <Route path="collections" element={<CollectionsAdmin />} />
          <Route path="collections/:name" element={<CollectionsAdmin />} />
          <Route path="system-integrations" element={<SystemIntegrationsAdmin />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  </LanguageProvider>
);

const App = () => (
  <HelmetProvider>
  <ThemeProvider defaultTheme="system">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
